import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { jobsApi } from '@/lib/api-client';
import { usePendingStories } from '../hooks/usePendingStories';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';

/**
 * Global component that monitors all pending story generation jobs
 * Continues polling even when user navigates away from generation page
 */
export function GlobalJobMonitor() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { pending, removePending } = usePendingStories();

  // Poll for all pending jobs
  const { data: jobs } = useQuery({
    queryKey: ['pending-jobs', pending.map(p => p.jobId)],
    queryFn: async () => {
      if (pending.length === 0) return [];

      // Fetch status for all pending jobs
      const jobPromises = pending.map(p =>
        jobsApi.getJobStatus(p.jobId).catch(() => null)
      );

      return Promise.all(jobPromises);
    },
    enabled: pending.length > 0,
    refetchInterval: 15000, // Poll every 15 seconds
    refetchIntervalInBackground: true, // Keep polling even when tab is not focused
  });

  // Handle job completions
  useEffect(() => {
    if (!jobs) return;

    jobs.forEach((job, index) => {
      if (!job) return;

      const pendingStory = pending[index];
      if (!pendingStory) return;

      // Handle completed job
      if (job.status === 'completed') {
        const storyId = job.result_data?.job_output?.story_id;
        const storyTitle = job.result_data?.job_output?.title;

        if (storyId) {
          // Show notification with click-to-navigate
          notifications.show({
            id: `story-complete-${job.id}`,
            title: 'Story Ready!',
            message: storyTitle
              ? `"${storyTitle}" is ready to read`
              : `Your ${pendingStory.themeName} story is ready`,
            color: 'teal',
            autoClose: false,
            onClick: () => {
              navigate(`/story/${storyId}`);
              notifications.hide(`story-complete-${job.id}`);
            },
          });

          // Invalidate queries to refresh library
          queryClient.invalidateQueries({ queryKey: ['library'] });
          queryClient.invalidateQueries({ queryKey: ['stories'] });
        }

        // Remove from pending list
        removePending(pendingStory.jobId);
      }

      // Handle failed job
      if (job.status === 'failed') {
        const errorMessage = job.result_data?.error_details?.error_message || 'Story generation failed';

        notifications.show({
          title: 'Generation Failed',
          message: `${pendingStory.themeName}: ${errorMessage}`,
          color: 'red',
        });

        // Remove from pending list
        removePending(pendingStory.jobId);
      }
    });
  }, [jobs, pending, removePending, navigate, queryClient]);

  // This component doesn't render anything
  return null;
}
